import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Upload, message, Image } from 'antd';
import { PlusOutlined, LoadingOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { fileUpload } from "../utils/storageUtils";
import { t } from '../i18n';

type FileType = File;

/**
 * 将文件转换为Base64格式
 * @param file 要转换的文件
 * @returns Promise<string> Base64字符串
 */
const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = (error) => reject(error);
    });

/**
 * ImageUploader组件的属性接口
 */
interface ImageUploaderProps {
    value?: string[];                    // 存储的URL或objectKey数组
    displayUrls?: string[];              // 展示URL数组（可选）
    onChange?: (urls: string[]) => void; // 值变化回调
    onError?: (error: Error, file: File) => void; // 错误回调
    maxCount?: number;                   // 最大文件数量
    accept?: string;                     // 接受的文件类型
    maxSizeMB?: number;                  // 最大文件大小
    disabled?: boolean;                  // 是否禁用
    tag?: string;                         // 标签
}

// 默认接受的文件类型
const defaultAccept = '.jpg,.jpeg,.png,.gif';

/**
 * 创建UploadFile对象
 * @param url 文件URL
 * @param index 文件索引
 * @param objectKey 对象键值
 * @returns UploadFile对象
 */
const createFileObject = (url: string, index: number, objectKey: string): UploadFile => ({
    uid: `-${index}`,
    name: `image-${index}${url.substring(url.lastIndexOf('.'))}`,
    status: 'done' as const,
    url,
    thumbUrl: url,
    response: { objectKey }
});

/**
 * 从文件列表中提取URL数组
 * @param files UploadFile数组
 * @returns 文件URL数组
 */
const extractFileUrls = (files: UploadFile[]): string[] => {
    return files
        .filter(file => file.status === 'done')
        .map(file => file.response?.objectKey || file.url || file.thumbUrl);
};

/**
 * 清理Blob URL，防止内存泄漏
 * @param file UploadFile对象
 */
const cleanupBlobUrl = (file: UploadFile) => {
    if (file.url && file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
    }
};

/**
 * 上传单个文件
 * @param file 要上传的文件
 * @param onError 错误回调函数
 * @returns Promise<UploadFile> 上传后的文件对象
 */
const uploadSingleFile = async (file: UploadFile, onError?: (error: Error, file: File) => void, tag?: string): Promise<UploadFile> => {
    if (!file.originFileObj) return file;

    try {
        // 调用文件上传服务
        const { objectKey } = await fileUpload(file.originFileObj, tag);
        // 创建Blob URL用于预览
        const blobUrl = URL.createObjectURL(file.originFileObj);
        return {
            ...file,
            status: 'done' as const,
            url: blobUrl,        // 使用Blob URL用于预览
            thumbUrl: blobUrl,   // 使用Blob URL用于缩略图
            response: { objectKey }
        };
    } catch (error) {
        // 处理上传错误
        console.error('Upload failed for file:', file.name, error);
        const errorMessage = error instanceof Error ? error.message : t('components.upload.uploadFailed');
        message.error(errorMessage);
        onError?.(error instanceof Error ? error : new Error(t('components.upload.uploadFailed')), file.originFileObj);

        return {
            ...file,
            status: 'error' as const,
            url: '',
            thumbUrl: ''
        };
    }
};

/**
 * 图片上传组件
 * 支持多文件上传、预览、删除等功能
 */
const ImageUploader: React.FC<ImageUploaderProps> = ({
                                                         value = [],
                                                         displayUrls = [],
                                                         onChange,
                                                         onError,
                                                         maxCount = 1,
                                                         accept = defaultAccept,
                                                         maxSizeMB = undefined,
                                                         disabled = false,
                                                         tag = undefined,
                                                     }) => {
    // 预览相关状态
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    // 文件列表和上传状态
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [isUploading, setIsUploading] = useState(false);

    /**
     * 从props构建文件列表
     * 处理displayUrls和value的映射关系
     */
    const fileListFromProps = useMemo(() => {
        const hasDisplayUrls = (displayUrls?.length || 0) > 0;
        const hasValue = (value?.length || 0) > 0;

        // 如果有displayUrls且与value长度一致，使用displayUrls作为展示URL
        if (hasDisplayUrls && (value?.length || 0) === (displayUrls?.length || 0)) {
            return displayUrls.map((url, index) => createFileObject(url, index, value[index]));
        }
        // 单文件模式且有value时，优先使用displayUrls，否则使用value
        else if (maxCount === 1 && hasValue) {
            const urls = hasDisplayUrls ? displayUrls : value;
            return urls.map((url, index) => createFileObject(url, index, value[index]));
        }
        return [];
    }, [value, displayUrls, maxCount]);

    /**
     * 当前文件列表
     * 优先使用内部状态，否则使用props构建的列表
     */
    const currentFileList = useMemo(() => {
        const hasInternalState = fileList.length > 0 ||
            (fileList.length === 0 && (value?.length || 0) === 0 && (displayUrls?.length || 0) === 0);

        return hasInternalState ? fileList : fileListFromProps;
    }, [fileListFromProps, fileList, value, displayUrls]);

    /**
     * 处理文件预览
     * 支持本地文件和远程文件的预览
     */
    const handlePreview = useCallback(async (file: UploadFile) => {
        // 如果没有URL和预览，且是本地文件，则生成Base64预览
        if (!file.url && !file.preview && file.originFileObj) {
            try {
                file.preview = await getBase64(file.originFileObj as File);
            } catch (error) {
                console.error('Failed to generate preview:', error);
                message.error(t('components.upload.failedToGeneratePreview'));
                return;
            }
        }
        // 设置预览图片并打开预览
        setPreviewImage(file.url || (file.preview as string));
        setPreviewOpen(true);
    }, []);

    /**
     * 处理文件上传变化
     * 包括文件上传和删除操作
     */
    const handleUploadChange: UploadProps['onChange'] = useCallback(async ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
        // 过滤出需要上传的文件（新选择的文件）
        const filesToUpload = newFileList.filter(file => file.originFileObj && file.status !== 'done' && file.status !== 'error');

        if (filesToUpload.length > 0) {
            // 有文件需要上传
            setIsUploading(true);
            try {
                // 只上传需要上传的文件。
                //
                // ⚠️ 这里必须跳过已 done 的：uploadSingleFile 只在 `!originFileObj` 时早返回，
                // 而已上传成功的文件 spread 后仍保留 originFileObj —— 原先无条件 map 整个
                // newFileList，导致每加一张图就把之前所有图重传一遍（N 张累计 N(N+1)/2 次上传，
                // 且每轮都换新 objectKey，早前那些成为孤儿对象）。
                //
                // 真正的危害不是流量：重传时任一张失败会被置成 status='error'、url=''，
                // 而 extractFileUrls 只收 status==='done' —— 那张图会**静默掉出**回传的 key 列表。
                // 对「凭证必填」的场景（如揽收地址异常上报，凭证是事后抽检唯一实物依据）
                // 就是证据链缺失，且界面上看不出少了什么。
                //
                // 编辑既有图不受影响：那些图来自 props.value/displayUrls，createFileObject
                // 不带 originFileObj，本来就走早返回。
                const uploadPromises = newFileList.map(file =>
                    file.status === 'done' ? Promise.resolve(file) : uploadSingleFile(file, onError, tag));
                const updatedFileList = await Promise.all(uploadPromises);

                setFileList(updatedFileList);
                // 延迟调用onChange，避免在渲染过程中调用
                setTimeout(() => onChange?.(extractFileUrls(updatedFileList)), 0);
            } finally {
                setIsUploading(false);
            }
        } else {
            // 没有文件需要上传（如删除操作）
            setFileList(newFileList);
            onChange?.(extractFileUrls(newFileList));
        }
    }, [onChange, onError]);

    /**
     * 文件上传前的验证
     * 检查文件类型和大小
     */
    const beforeUpload = useCallback((file: File) => {
        // 检查文件类型是否为图片
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error(t('components.upload.onlyImageFilesAllowed'));
            return Upload.LIST_IGNORE; // 完全阻止文件被添加
        }

        // 检查文件大小是否超过限制（如果传入了maxSizeMB）
        if (maxSizeMB !== undefined) {
            const isLtMaxSize = file.size / 1024 / 1024 < maxSizeMB;
            if (!isLtMaxSize) {
                message.error(t('components.upload.imageSizeLimit', { maxSize: maxSizeMB }));
                return Upload.LIST_IGNORE; // 完全阻止文件被添加
            }
        }
        return false; // 阻止自动上传，使用自定义上传逻辑
    }, [maxSizeMB]);

    /**
     * 处理文件删除
     * 清理Blob URL并允许删除
     */
    const handleRemove = useCallback((file: UploadFile) => {
        cleanupBlobUrl(file);
        return true;
    }, []);

    // 当父组件重置 value 为空时，同步清空内部 fileList，避免缓存上一次上传的图片
    useEffect(() => {
        if (!value || value.length === 0) {
            setFileList([]);
        }
    }, [value]);

    /**
     * 组件卸载时清理所有Blob URL
     * 防止内存泄漏
     *
     * ⚠️ 依赖数组必须是 `[]`，不能是 `[fileList]`。
     *
     * React 在 fileList 从 A 变 B 时会执行「用 A 创建的那个 effect」的清理函数，
     * 即 revoke 掉 A 里全部 blob URL；而 B 里未变动的那些项引用的是**同一个 URL 字符串**，
     * 被 revoke 后缩略图当场变裂图。
     *
     * 这个隐患长期被 handleUploadChange 无条件重传掩盖着 —— 那时每一轮都会给每张图
     * 重新 createObjectURL，B 里全是新 URL，revoke A 的旧 URL 自然无害。一旦上传改成
     * 「已 done 的跳过」（见 handleUploadChange 的说明），已传图就会保留原 URL，隐患立刻显形：
     * 传第 2 张时第 1 张裂、传第 3 张时第 2 张裂。
     *
     * 删除单个文件的回收由 handleRemove 单独承担，所以改成 `[]` 不会漏回收。
     * 用 ref 读最新 fileList：卸载时要清的是**当时**的列表，而不是 effect 创建那一刻的空列表。
     */
    const fileListRef = useRef(fileList);
    // 在 commit 后同步而非 render 期直接赋值：React 明确要求不在渲染阶段读写 ref，
    // 被丢弃的渲染会把从未提交过的列表写进去。
    useEffect(() => {
        fileListRef.current = fileList;
    }, [fileList]);
    useEffect(() => {
        return () => {
            fileListRef.current.forEach(cleanupBlobUrl);
        };
    }, []);

    /**
     * 自定义渲染上传项：图片覆盖层中间放预览，右上角放删除
     */
    const renderUploadItem = useCallback<NonNullable<UploadProps['itemRender']>>(
        (_originNode, file, _fileList, actions) => {
            // 上传中的状态直接返回默认节点，保持 loading 样式
            if (file.status && file.status !== 'done' && file.status !== 'error') {
                return _originNode;
            }

            const fileUrl = file.url || file.thumbUrl;

            const handlePreviewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                handlePreview(file);
            };

            const handleRemoveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                if (!disabled) {
                    actions?.remove?.();
                }
            };

            return (
                <div className="relative w-full h-full rounded border border-gray-200 overflow-hidden bg-gray-100 group">
                    {fileUrl ? (
                        <img src={fileUrl} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                        _originNode
                    )}

                    {/* 预览按钮（居中） */}
                    <button
                        type="button"
                        className="absolute inset-0 flex items-center justify-center text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity bg-black/40"
                        onClick={handlePreviewClick}
                        aria-label={t('components.upload.preview') || 'Preview'}
                    >
                        <EyeOutlined />
                    </button>

                    {/* 删除按钮（右上角）- 禁用状态下隐藏 */}
                    {!disabled && (
                        <button
                            type="button"
                            className="absolute top-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={handleRemoveClick}
                            aria-label={t('components.upload.delete') || 'Delete'}
                        >
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            );
        },
        [handlePreview, disabled]
    );

    /**
     * 上传按钮
     * 根据上传状态显示不同的按钮样式
     */
    const uploadButton = useMemo(() => {
        if (isUploading) {
            // 上传中状态：显示加载图标和文字
            return (
                <button style={{ border: 0, background: 'none' }} type="button">
                    <LoadingOutlined style={{ fontSize: '26px', color: '#1677ff' }} />
                    <div style={{ marginTop: 8, color: '#000000' }}>{t('components.upload.uploadIng')}</div>
                </button>
            );
        }
        // 正常状态：显示加号图标和上传文字
        return (
            <button style={{ border: 0, background: 'none' }} type="button">
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>{t('components.upload.upload')}</div>
            </button>
        );
    }, [isUploading]);

    return (
        <>
            <Upload
                listType="picture-card"
                fileList={currentFileList}
                onPreview={handlePreview}
                onChange={handleUploadChange}
                onRemove={handleRemove}
                showUploadList={{ showPreviewIcon: false, showRemoveIcon: false }}
                itemRender={renderUploadItem}
                maxCount={maxCount}
                accept={accept}
                disabled={disabled || isUploading}
                beforeUpload={beforeUpload}
            >
                {/* 当文件数量达到最大限制时，不显示上传按钮 */}
                {currentFileList.length >= maxCount ? null : uploadButton}
            </Upload>

            {/* 图片预览组件 */}
            {previewImage && (
                <Image
                    wrapperStyle={{ display: 'none' }}
                    preview={{
                        visible: previewOpen,
                        onVisibleChange: (visible) => setPreviewOpen(visible),
                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                    }}
                    src={previewImage}
                />
            )}
        </>
    );
};

export default ImageUploader;